import { computed, watch, type ComputedRef, type Ref, ref } from 'vue'

// ---------------------------------------------------------------------------
// useLandingTheme — one source of truth for the visitor's light/dark choice
// ---------------------------------------------------------------------------
//
// SRP: this composable owns theme state, persistence, OS-preference tracking,
// and DOM broadcast. Consumers just READ `paintedTheme` / `isLight` — nobody
// else stamps localStorage, listens to `prefers-color-scheme`, or writes
// `data-theme` to the DOM.
//
// DIP: layouts and pages depend on this abstraction (a boolean + a ref) and
// on the global CSS contract (`html[data-theme]`) — never on where the choice
// came from. Adding a new page that honours the theme = read `isLight` OR
// consume `var(--zav-header-bg)` from CSS. That's it.
//
// OCP: adding new theme dimensions (e.g. a "high-contrast" variant) means
// widening `ThemeChoice` + adding a branch in `paintedTheme` + adding CSS
// rules — no consumer edits needed.

export type ThemeChoice = 'light' | 'dark' | 'system'

// Persisted under the SAME key the landing has always used so returning
// visitors keep whatever preference they set on the previous build.
const THEME_KEY = 'zaveato_landing_theme'

// Module-level reactive state. Held here (not per-composable-call) so every
// consumer subscribes to the same ref — one visitor, one theme.
const themeChoice = ref<ThemeChoice>('system')
const systemPrefersDark = ref(false)

// The resolved theme actually painted on the page. Always `light | dark`
// (never `system`) so CSS only needs to handle two states.
const paintedTheme = computed<'light' | 'dark'>(() => {
  if (themeChoice.value === 'system') return systemPrefersDark.value ? 'dark' : 'light'
  return themeChoice.value
})

// One-time module init: restore from localStorage + attach the OS-preference
// listener + stamp the DOM. Runs SYNCHRONOUSLY at module import when
// `window` is available so the very first render sees the correct theme —
// waiting for onMounted produced a flash of the wrong theme.
let initialised = false

function onSystemThemeChange(e: MediaQueryListEvent) {
  systemPrefersDark.value = e.matches
}

/**
 * Broadcast the resolved theme to `<html>` — both as `data-theme` (for our
 * own CSS in `styles/theme.css`) and as the standard `.dark` class (so any
 * `dark:*` Tailwind utilities in the codebase pick it up automatically).
 *
 * This is what makes the theme WORK on every page without each layout
 * having to opt in — global CSS + Tailwind's class strategy do the paint.
 */
function applyThemeToDom(theme: 'light' | 'dark') {
  if (typeof document === 'undefined') return
  const html = document.documentElement
  html.dataset.theme = theme
  html.classList.toggle('dark', theme === 'dark')
  // `color-scheme` tells the UA to swap default form controls / scrollbars
  // to the matching palette — without it, native widgets look mismatched.
  html.style.colorScheme = theme
}

function ensureInitialised() {
  if (initialised || typeof window === 'undefined') return
  initialised = true

  try {
    const saved = localStorage.getItem(THEME_KEY)
    if (saved === 'light' || saved === 'dark' || saved === 'system') {
      themeChoice.value = saved
    }
  } catch { /* private mode / disabled storage — fall through to 'system' */ }

  if (window.matchMedia) {
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    systemPrefersDark.value = mq.matches
    mq.addEventListener('change', onSystemThemeChange)
  }

  // Immediate stamp so the FIRST paint has the right attribute + class,
  // then keep it in sync as choice / OS pref changes.
  applyThemeToDom(paintedTheme.value)
  watch(paintedTheme, applyThemeToDom)
}

// Fire the init at module load. Guarded on `window` for SSR / prerender.
if (typeof window !== 'undefined') {
  ensureInitialised()
}

function setTheme(choice: ThemeChoice) {
  themeChoice.value = choice
  try {
    localStorage.setItem(THEME_KEY, choice)
  } catch { /* ignore — the paint still updates via the reactive chain */ }
}

interface UseLandingThemeReturn {
  themeChoice: Ref<ThemeChoice>
  paintedTheme: ComputedRef<'light' | 'dark'>
  isLight: ComputedRef<boolean>
  setTheme: (choice: ThemeChoice) => void
}

export function useLandingTheme(): UseLandingThemeReturn {
  return {
    themeChoice,
    paintedTheme,
    isLight: computed(() => paintedTheme.value === 'light'),
    setTheme,
  }
}
